import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import { getActivityStatus, getStatusColor, ACTIVITY_STATUS } from '../utils/activityStatus.js'
import RevealPanel from './RevealPanel.jsx'

// ─── Activity category colors (mirrors TravelAppProper.jsx) ──────────────────
const getActivityColor = (category) => {
  const colors = {
    food: '#E74C3C', cultural: '#9B59B6', monuments: '#34495E',
    activities: '#27AE60', locals: '#F39C12', shopping: '#E67E22',
    nature: '#16A085', nightlife: '#8E44AD', transport: '#3498DB',
    challenge: '#FFD700', default: '#D4A574'
  }
  return colors[category] || colors.default
}

// ─── Haversine distance (km) ─────────────────────────────────────────────────
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// ─── City center from median of geocoded activities (robust against outliers) ─
const medianOf = (arr) => {
  const s = [...arr].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

const getCityCenter = (cityData) => {
  const all = [
    ...(cityData?.culturalAttractions || []),
    ...(cityData?.monuments || []),
    ...(cityData?.localActivities || []),
    ...(cityData?.traditionalLocals || []),
  ].filter(a => a.lat !== undefined && a.lng !== undefined)
  if (all.length < 2) return null
  return { lat: medianOf(all.map(a => a.lat)), lng: medianOf(all.map(a => a.lng)) }
}

// ─── Collect all unscheduled city activities ─────────────────────────────────
const getUnscheduledActivities = (cityData, timeline) => {
  if (!cityData) return []
  const scheduled = new Set(Object.values(timeline).flat().map(a => a.name))
  return [
    ...(cityData.culturalAttractions || []).map(a => ({ ...a, category: 'cultural' })),
    ...(cityData.monuments || []).map(a => ({ ...a, category: 'monuments' })),
    ...(cityData.localActivities || []).map(a => ({ ...a, category: 'activities' })),
    ...(cityData.traditionalLocals || []).map(a => ({ ...a, category: 'locals' })),
  ].filter(a => !scheduled.has(a.name))
}

// ─── Slot card (filled) ──────────────────────────────────────────────────────
function SlotCard({ activity, tripDay, userData, period, onWriteNote, onReschedule, onRemove }) {
  const { t } = useTranslation()
  const status = getActivityStatus(activity, tripDay, userData)
  const dotColor = getStatusColor(status, activity.category, getActivityColor)
  const isPast = status !== ACTIVITY_STATUS.PLANNED

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '12px 14px',
      marginBottom: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      border: `1px solid ${status === ACTIVITY_STATUS.UNVISITED ? '#FFE0B2' : status === ACTIVITY_STATUS.VISITED ? '#C8E6C9' : '#f0ece6'}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Status dot */}
        <div style={{
          width: '12px', height: '12px', borderRadius: '50%',
          background: dotColor, flexShrink: 0
        }} />
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px', fontWeight: '700', color: '#8B7355',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>{activity.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
            <span style={{
              background: getActivityColor(activity.category),
              color: 'white', borderRadius: '6px',
              padding: '1px 6px', fontSize: '9px', fontWeight: '700',
              textTransform: 'uppercase', letterSpacing: '0.3px'
            }}>{activity.category}</span>
            {status === ACTIVITY_STATUS.VISITED && (
              <span style={{ fontSize: '9px', color: '#4CAF50', fontWeight: '700' }}>✓ {t('notes.dayDetail.visited')}</span>
            )}
            {status === ACTIVITY_STATUS.UNVISITED && (
              <span style={{ fontSize: '9px', color: '#FF9800', fontWeight: '700' }}>! {t('notes.dayDetail.unvisited')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Unvisited action buttons */}
      {status === ACTIVITY_STATUS.UNVISITED && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => onWriteNote(activity)}
            style={{
              flex: 1, minWidth: '80px', padding: '7px 8px', fontSize: '11px', fontWeight: '600',
              background: '#C4714A', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer'
            }}
          >{t('notes.dayDetail.writeNote')}</button>
          <button
            onClick={() => onReschedule(activity, period)}
            style={{
              flex: 1, minWidth: '80px', padding: '7px 8px', fontSize: '11px', fontWeight: '600',
              background: 'transparent', color: '#8B7355', border: '1.5px solid #C4A882',
              borderRadius: '10px', cursor: 'pointer'
            }}
          >{t('notes.dayDetail.reschedule')}</button>
          <button
            onClick={() => onRemove(activity, period)}
            style={{
              padding: '7px 10px', fontSize: '11px', fontWeight: '600',
              background: 'transparent', color: '#E74C3C', border: '1.5px solid #FFCDD2',
              borderRadius: '10px', cursor: 'pointer'
            }}
          >{t('notes.dayDetail.remove')}</button>
        </div>
      )}

      {/* Reveal layer — appears silently once the traveller has been near this stop */}
      <RevealPanel poi={activity} />
    </div>
  )
}

// ─── Empty slot placeholder ───────────────────────────────────────────────────
function EmptySlot({ t }) {
  return (
    <div style={{
      border: '1.5px dashed #d4c9b8', borderRadius: '12px',
      padding: '12px 14px', marginBottom: '8px', textAlign: 'center',
      color: '#C4A882', fontSize: '12px'
    }}>+ {t('notes.dayDetail.emptySlot')}</div>
  )
}

// ─── Stepper control ──────────────────────────────────────────────────────────
function Stepper({ value, min, max, onChange, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
      <span style={{ fontSize: '13px', color: '#8B7355', flex: 1 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          style={{
            width: '28px', height: '28px', borderRadius: '50%', border: '1.5px solid #C4A882',
            background: 'white', color: '#8B7355', fontSize: '16px', cursor: value <= min ? 'default' : 'pointer',
            opacity: value <= min ? 0.35 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >−</button>
        <span style={{ fontSize: '18px', fontWeight: '700', color: '#8B7355', minWidth: '16px', textAlign: 'center' }}>{value}</span>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          style={{
            width: '28px', height: '28px', borderRadius: '50%', border: '1.5px solid #C4A882',
            background: value >= max ? '#f0f0f0' : '#B8A082', color: value >= max ? '#999' : 'white',
            fontSize: '16px', cursor: value >= max ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >+</button>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DayDetailModal({ isOpen, tripDay, userData, onClose, onNavigate, onDataChanged }) {
  const { t, i18n } = useTranslation()

  // Core state
  const [currentDay, setCurrentDay] = useState(tripDay)
  const [subView, setSubView] = useState('detail')      // 'detail' | 'planner' | 'reschedule'
  const [timeline, setTimeline] = useState({})
  const [reschedulePicker, setReschedulePicker] = useState(null) // { activity, period }
  const [rescheduleError, setRescheduleError] = useState('')

  // Planner state
  const [cityData, setCityData] = useState(null)
  const [cityLoading, setCityLoading] = useState(false)
  const cityLoadAttempted = useRef(false)
  const [morningCount, setMorningCount] = useState(0)
  const [afternoonCount, setAfternoonCount] = useState(0)
  const [selectionOrder, setSelectionOrder] = useState([])
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true)
  const [leafletModule, setLeafletModule] = useState(null)

  // Leaflet map
  const leafletRef = useRef(null)
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersMapRef = useRef({})   // name → { marker, category }
  const selectHandlerRef = useRef(null) // always points at the latest handleActivitySelect (markers are bound once)

  // Reschedule day/period picker state
  const [rescheduleDay, setRescheduleDay] = useState(null)
  const [reschedulePeriod, setReschedulePeriod] = useState('morning')

  const tripDuration = userData?.endDate && userData?.startDate
    ? Math.ceil((new Date(userData.endDate) - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1
    : userData?.totalDays || 7

  // Load timeline on mount and day change
  useEffect(() => {
    if (!isOpen) return
    const td = Storage.getTripData()
    setTimeline(td.timeline || {})
  }, [isOpen, currentDay])

  // Sync tripDay prop on open
  useEffect(() => {
    if (isOpen) {
      setCurrentDay(tripDay)
      setSubView('detail')
      setSelectionOrder([])
      setReschedulePicker(null)
    }
  }, [isOpen, tripDay])

  // Online/offline detection
  useEffect(() => {
    const onOnline = () => setIsOnline(true)
    const onOffline = () => setIsOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline) }
  }, [])

  // Load city data when entering planner
  useEffect(() => {
    if (subView !== 'planner' || cityData || cityLoading || cityLoadAttempted.current) return
    cityLoadAttempted.current = true
    setCityLoading(true)
    const lang = i18n.language || 'en'
    const langFolder = ['es', 'fr'].includes(lang) ? `${lang}/` : ''
    const destination = (userData?.destination || '').toLowerCase()

    fetch('./TRIP/cities-index.json')
      .then(r => r.json())
      .then(indexData => {
        const entry = (indexData.cities || []).find(c => c.city.toLowerCase() === destination)
        if (!entry) { setCityData(null); setCityLoading(false); return }
        return fetch(`./TRIP/${langFolder}${entry.file}.json`)
          .then(r => r.json())
          .then(data => {
            const city = (data.cities || []).find(c => c.city.toLowerCase() === destination)
            setCityData(city || null)
            setCityLoading(false)
          })
      })
      .catch(() => { setCityData(null); setCityLoading(false) })
  }, [subView, cityData, cityLoading, userData, i18n.language])

  // Leaflet map init — sets state to trigger map mount effect
  useEffect(() => {
    if (subView !== 'planner' || !isOnline || leafletModule) return
    import('leaflet').then(mod => {
      const L = mod.default
      if (!document.querySelector('link[data-leaflet-css]')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        link.setAttribute('data-leaflet-css', '')
        document.head.appendChild(link)
      }
      leafletRef.current = L
      setLeafletModule(L)
    }).catch(() => {})
  }, [subView, isOnline, leafletModule])

  // Mount map once leaflet + container + cityData ready
  useEffect(() => {
    const L = leafletModule
    if (!L || !mapContainerRef.current || !cityData || mapInstanceRef.current) return
    const center = getCityCenter(cityData)
    if (!center) return

    const map = L.map(mapContainerRef.current, { zoomControl: true })
    map.setView([center.lat, center.lng], 13)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19
    }).addTo(map)

    const unscheduled = getUnscheduledActivities(cityData, timeline)
    const geoActs = unscheduled.filter(a => a.lat !== undefined && a.lng !== undefined)
    markersMapRef.current = {}

    geoActs.forEach(act => {
      const marker = L.circleMarker([act.lat, act.lng], {
        radius: 10, fillColor: getActivityColor(act.category),
        color: 'white', weight: 2, fillOpacity: 0.9
      }).addTo(map)
      marker.bindTooltip(act.name, { permanent: false, direction: 'top' })
      marker.on('click', () => selectHandlerRef.current?.(act))
      markersMapRef.current[act.name] = { marker, category: act.category }
    })

    mapInstanceRef.current = map

    // After paint: fix size then fit all markers into view
    setTimeout(() => {
      map.invalidateSize()
      if (geoActs.length > 0) {
        map.fitBounds(
          L.latLngBounds(geoActs.map(a => [a.lat, a.lng])),
          { padding: [30, 30], maxZoom: 15 }
        )
      } else {
        map.setView([center.lat, center.lng], 13)
      }
    }, 200)

    return () => {
      markersMapRef.current = {}
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null }
    }
  }, [leafletModule, cityData, timeline])

  // Update marker styles when selection changes
  useEffect(() => {
    const entries = markersMapRef.current
    if (!entries || !Object.keys(entries).length) return
    Object.entries(entries).forEach(([name, { marker, category }]) => {
      const selIdx = selectionOrder.findIndex(a => a.name === name)
      const isSelected = selIdx !== -1
      marker.setStyle({
        radius: isSelected ? 14 : 10,
        fillColor: isSelected ? '#D4A574' : getActivityColor(category),
        color: isSelected ? '#8B5E2E' : 'white',
        weight: isSelected ? 3 : 2,
        fillOpacity: 0.95
      })
      marker.setTooltipContent(isSelected ? `${selIdx + 1}. ${name}` : name)
    })
  }, [selectionOrder])

  // Reset planner on subView change
  useEffect(() => {
    if (subView !== 'planner') {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
      setSelectionOrder([])
      setMorningCount(0)
      setAfternoonCount(0)
    }
  }, [subView])

  if (!isOpen) return null

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const morningSlots = timeline[`day${currentDay}_morning`] || []
  const afternoonSlots = timeline[`day${currentDay}_afternoon`] || []
  const morningFree = 3 - morningSlots.length
  const afternoonFree = 3 - afternoonSlots.length

  const getDateForDay = (day) => {
    if (!userData?.startDate) return `Day ${day}`
    const d = new Date(userData.startDate)
    d.setDate(d.getDate() + day - 1)
    return d.toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })
  }

  const reloadTimeline = () => {
    const td = Storage.getTripData()
    setTimeline(td.timeline || {})
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  const handleWriteNote = (activity) => {
    onClose()
    window.navigationOptions = {
      directToWriteMemory: true,
      activityId: activity.id,
      preselectedDay: currentDay,
      prefilledTitle: activity.name
    }
    onNavigate('notes')
  }

  const handleRemove = (activity, period) => {
    if (!window.confirm(t('notes.dayDetail.confirmRemove'))) return
    const td = Storage.getTripData()
    const key = `day${currentDay}_${period}`
    td.timeline[key] = (td.timeline[key] || []).filter(a => a.id !== activity.id)
    td.notes = (td.notes || []).filter(n => n.id !== activity.id)
    window.__cachedTripData = td
    Storage.saveTripData(td)
    reloadTimeline()
    onDataChanged()
  }

  const handleOpenReschedule = (activity, period) => {
    setReschedulePicker({ activity, period })
    setRescheduleDay(currentDay)
    setReschedulePeriod('morning')
    setRescheduleError('')
    setSubView('reschedule')
  }

  const handleConfirmReschedule = () => {
    if (!rescheduleDay || !reschedulePeriod || !reschedulePicker) return
    const { activity, period: fromPeriod } = reschedulePicker
    const fromKey = `day${currentDay}_${fromPeriod}`
    const toKey = `day${rescheduleDay}_${reschedulePeriod}`
    const td = Storage.getTripData()
    if ((td.timeline[toKey] || []).length >= 3) {
      setRescheduleError(t('notes.dayDetail.rescheduleSlotFull'))
      return
    }
    td.timeline[fromKey] = (td.timeline[fromKey] || []).filter(a => a.id !== activity.id)
    td.timeline[toKey] = [...(td.timeline[toKey] || []), activity]
    const noteIdx = (td.notes || []).findIndex(n => n.id === activity.id)
    if (noteIdx !== -1) {
      td.notes[noteIdx].scheduledDay = rescheduleDay
      td.notes[noteIdx].scheduledPeriod = reschedulePeriod
      td.notes[noteIdx].tripDay = rescheduleDay
    }
    window.__cachedTripData = td
    Storage.saveTripData(td)
    reloadTimeline()
    setReschedulePicker(null)
    setSubView('detail')
    onDataChanged()
  }

  const handleActivitySelect = (act) => {
    const total = morningCount + afternoonCount
    if (total === 0) return
    setSelectionOrder(prev => {
      const idx = prev.findIndex(a => a.name === act.name)
      if (idx !== -1) return prev.filter(a => a.name !== act.name)
      if (prev.length < total) return [...prev, act]
      return prev
    })
  }
  // Keep the ref pointing at the latest handler so map markers (bound once) don't
  // call a stale closure with outdated morning/afternoon counts or selection.
  selectHandlerRef.current = handleActivitySelect

  const handleConfirmPlanning = () => {
    const td = Storage.getTripData()
    const morning = selectionOrder.slice(0, morningCount)
    const afternoon = selectionOrder.slice(morningCount)
    ;[['morning', morning], ['afternoon', afternoon]].forEach(([period, acts]) => {
      const key = `day${currentDay}_${period}`
      acts.forEach(act => {
        const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36).slice(2)
        const entry = {
          id, name: act.name, category: act.category,
          description: act.description || '', mapsLink: act.mapsLink || '',
          // Preserve geo + authored reveal so proximity/serendipity and the
          // reveal layer work for scheduled stops (all optional, tolerated absent).
          ...(typeof act.lat === 'number' ? { lat: act.lat } : {}),
          ...(typeof act.lng === 'number' ? { lng: act.lng } : {}),
          ...(act.reveal ? { reveal: act.reveal } : {}),
          ...(act.hidden ? { hidden: true } : {})
        }
        td.timeline[key] = [...(td.timeline[key] || []), entry]
        td.notes = [...(td.notes || []), {
          id, type: 'todo', title: `TO DO: ${act.name}`,
          content: `Scheduled for Day ${currentDay} - ${period}`,
          photos: [], tags: ['todo', act.category, 'activity'],
          date: new Date().toISOString().split('T')[0],
          tripDay: currentDay, isActivity: true, activityCategory: act.category,
          mapsLink: act.mapsLink || '', scheduledDay: currentDay, scheduledPeriod: period
        }]
      })
    })
    window.__cachedTripData = td
    Storage.saveTripData(td)
    reloadTimeline()
    setSubView('detail')
    setSelectionOrder([])
    setMorningCount(0)
    setAfternoonCount(0)
    onDataChanged()
  }

  // ─── Reschedule week-grid picker ─────────────────────────────────────────────

  const renderReschedulePicker = () => {
    const { activity } = reschedulePicker || {}
    if (!userData?.startDate || !userData?.endDate) return null
    const startDate = new Date(userData.startDate)
    const dayLabels = [
      t('mainPage.daysOfWeek.MON'), t('mainPage.daysOfWeek.TUE'), t('mainPage.daysOfWeek.WED'),
      t('mainPage.daysOfWeek.THU'), t('mainPage.daysOfWeek.FRI'), t('mainPage.daysOfWeek.SAT'),
      t('mainPage.daysOfWeek.SUN')
    ]

    const getWeekStructure = () => {
      const endDate = new Date(userData.endDate)
      const firstMonday = new Date(startDate)
      const dow = firstMonday.getDay()
      firstMonday.setDate(firstMonday.getDate() - (dow === 0 ? 6 : dow - 1))
      const lastSunday = new Date(endDate)
      const edow = lastSunday.getDay()
      lastSunday.setDate(lastSunday.getDate() + (edow === 0 ? 0 : 7 - edow))
      const weeks = []
      const cur = new Date(firstMonday)
      while (cur <= lastSunday) {
        const weekEnd = new Date(cur); weekEnd.setDate(weekEnd.getDate() + 6)
        const hasTrip = !(weekEnd < startDate || cur > endDate)
        if (hasTrip) {
          const week = { weekNumber: weeks.length + 1, startDate: new Date(cur), days: [] }
          for (let i = 0; i < 7; i++) {
            const dd = new Date(cur); dd.setDate(dd.getDate() + i)
            const isInTrip = dd >= startDate && dd <= endDate
            const tday = isInTrip ? Math.floor((dd - startDate) / 86400000) + 1 : null
            week.days.push({ date: dd, dayOfWeek: dayLabels[i], tripDay: tday, isInTrip })
          }
          weeks.push(week)
        }
        cur.setDate(cur.getDate() + 7)
      }
      return weeks
    }

    const weeks = getWeekStructure()
    const td = Storage.getTripData()

    return (
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <button onClick={() => { setSubView('detail'); setReschedulePicker(null) }}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#8B7355', padding: '0 8px 0 0' }}>←</button>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {t('notes.dayDetail.rescheduleTo')}
            </div>
            <div style={{ fontSize: '13px', color: '#8B7355', fontWeight: '600' }}>{activity?.name}</div>
          </div>
        </div>

        {/* Period picker */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {['morning', 'afternoon'].map(p => {
            const key = `day${rescheduleDay}_${p}`
            const count = (td.timeline?.[key] || []).length
            const full = count >= 3
            return (
              <button key={p}
                onClick={() => !full && setReschedulePeriod(p)}
                disabled={full}
                style={{
                  flex: 1, padding: '10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                  border: reschedulePeriod === p ? '2px solid #B8A082' : '1.5px solid #e8e1d6',
                  background: reschedulePeriod === p ? '#f9f7f4' : 'white',
                  color: full ? '#ccc' : '#8B7355', cursor: full ? 'default' : 'pointer'
                }}
              >
                {p === 'morning' ? t('notes.dayDetail.morning') : t('notes.dayDetail.afternoon')}
                <div style={{ fontSize: '10px', color: full ? '#E74C3C' : '#B8A082', marginTop: '2px' }}>{count}/3</div>
              </button>
            )
          })}
        </div>

        {/* Day grid */}
        {weeks.map((week, wi) => (
          <div key={wi} style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
              {t('mainPage.week')} {week.weekNumber}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
              {week.days.map((day, di) => {
                const isSelected = rescheduleDay === day.tripDay
                const key = `day${day.tripDay}_${reschedulePeriod}`
                const full = day.isInTrip && (td.timeline?.[key] || []).length >= 3
                return (
                  <div key={di}
                    onClick={() => day.isInTrip && !full && setRescheduleDay(day.tripDay)}
                    style={{
                      borderRight: di < 6 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                      padding: '8px 4px', textAlign: 'center',
                      background: isSelected ? 'rgb(139 115 85 / 25%)' : day.isInTrip ? 'rgb(225 147 50 / 21%)' : 'rgba(0,0,0,0.02)',
                      opacity: day.isInTrip ? (full ? 0.4 : 1) : 0.3,
                      cursor: day.isInTrip && !full ? 'pointer' : 'default',
                      outline: isSelected ? '2px solid #8B7355' : 'none', outlineOffset: '-2px'
                    }}
                  >
                    <div style={{ fontSize: '8px', fontWeight: '700', color: '#8B7355' }}>{day.dayOfWeek}</div>
                    <div style={{ fontSize: '11px', color: '#333', fontWeight: '600' }}>{day.date.toLocaleDateString('en-US', { day: 'numeric' })}</div>
                    {day.isInTrip && <div style={{ fontSize: '7px', color: '#B8A082', fontWeight: '700' }}>{t('mainPage.day')} {day.tripDay}</div>}
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {rescheduleError && (
          <div style={{ color: '#E74C3C', fontSize: '12px', marginBottom: '10px', textAlign: 'center' }}>{rescheduleError}</div>
        )}

        <button onClick={handleConfirmReschedule}
          disabled={!rescheduleDay}
          style={{
            width: '100%', padding: '14px', borderRadius: '20px', border: 'none',
            background: rescheduleDay ? '#5B8FA8' : '#e0d8cc', color: 'white',
            fontSize: '14px', fontWeight: '700', cursor: rescheduleDay ? 'pointer' : 'default',
            letterSpacing: '0.5px'
          }}
        >{t('notes.dayDetail.reschedule')}</button>
      </div>
    )
  }

  // ─── Planner sub-view ────────────────────────────────────────────────────────

  const renderPlanner = () => {
    const total = morningCount + afternoonCount
    const unscheduled = getUnscheduledActivities(cityData, timeline)
      .filter(a => a.lat !== undefined && a.lng !== undefined)
    const center = getCityCenter(cityData)
    const sorted = center
      ? [...unscheduled].sort((a, b) =>
          haversineKm(center.lat, center.lng, a.lat, a.lng) - haversineKm(center.lat, center.lng, b.lat, b.lng)
        )
      : [...unscheduled].sort((a, b) => a.name.localeCompare(b.name))

    return (
      <div style={{ padding: '0 20px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <button onClick={() => setSubView('detail')}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#8B7355', padding: '0 8px 0 0' }}>←</button>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {t('notes.dayDetail.plannerTitle')}
            </div>
            <div style={{ fontSize: '13px', color: '#8B7355' }}>{t('notes.dayDetail.plannerSubtitle', { day: currentDay })}</div>
          </div>
        </div>

        {cityLoading && (
          <div style={{ textAlign: 'center', color: '#B8A082', padding: '20px', fontSize: '13px' }}>Loading city data...</div>
        )}

        {!cityLoading && !cityData && (
          <div style={{ textAlign: 'center', color: '#B8A082', padding: '20px', fontSize: '13px' }}>Could not load city data.</div>
        )}

        {!cityLoading && cityData && unscheduled.length === 0 && (
          <div style={{ textAlign: 'center', color: '#B8A082', padding: '20px', fontSize: '13px' }}>{t('notes.dayDetail.noUnscheduled')}</div>
        )}

        {!cityLoading && cityData && unscheduled.length > 0 && (
          <>
            {/* Slot count steppers */}
            <div style={{ background: 'white', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
              <Stepper
                label={t('notes.dayDetail.selectMorningCount')}
                value={morningCount} min={0} max={morningFree}
                onChange={v => { setMorningCount(v); setSelectionOrder([]) }}
              />
              <Stepper
                label={t('notes.dayDetail.selectAfternoonCount')}
                value={afternoonCount} min={0} max={afternoonFree}
                onChange={v => { setAfternoonCount(v); setSelectionOrder([]) }}
              />
              {total > 0 && (
                <div style={{ fontSize: '11px', color: '#B8A082', marginTop: '6px', textAlign: 'center' }}>
                  {t('notes.dayDetail.selectInOrder', { selected: selectionOrder.length, total })}
                </div>
              )}
            </div>

            {/* Online/offline indicator */}
            {!isOnline && (
              <div style={{ fontSize: '11px', color: '#B8A082', textAlign: 'center', marginBottom: '10px', fontStyle: 'italic' }}>
                {t('notes.dayDetail.offlineMode')}
              </div>
            )}

            {/* Leaflet map — tap a point to pick it (online only) */}
            {isOnline && leafletModule && (
              <>
                {total > 0 && (
                  <div style={{ fontSize: '11px', color: '#B8A082', textAlign: 'center', marginBottom: '8px', fontStyle: 'italic' }}>
                    {t('notes.dayDetail.tapMapHint')}
                  </div>
                )}
                <div style={{ borderRadius: '12px', marginBottom: '14px', overflow: 'hidden' }}>
                  <div ref={mapContainerRef} style={{ height: '220px' }} />
                </div>
              </>
            )}

            {/* Activity list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {sorted.map((act, i) => {
                const selIdx = selectionOrder.findIndex(a => a.name === act.name)
                const isSelected = selIdx !== -1
                const pickNumber = isSelected ? selIdx + 1 : null
                const canSelect = total > 0 && (isSelected || selectionOrder.length < total)
                const distKm = center && act.lat !== undefined && act.lng !== undefined
                  ? haversineKm(center.lat, center.lng, act.lat, act.lng)
                  : null
                const distLabel = distKm !== null
                  ? distKm < 1 ? `${Math.round(distKm * 1000)}m` : `${distKm.toFixed(1)}km`
                  : null
                return (
                  <div key={i}
                    onClick={() => canSelect && handleActivitySelect(act)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      background: isSelected ? '#f9f7f4' : 'white',
                      borderRadius: '12px', padding: '10px 12px',
                      border: isSelected ? '2px solid #B8A082' : '1px solid #f0ece6',
                      cursor: canSelect ? 'pointer' : 'default',
                      opacity: !canSelect && !isSelected ? 0.5 : 1,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                    }}
                  >
                    {/* Pick number badge */}
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                      background: isSelected ? '#5B8FA8' : '#f0ece6',
                      color: isSelected ? 'white' : '#B8A082',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: '700'
                    }}>
                      {pickNumber || (i + 1)}
                    </div>
                    {/* Category dot */}
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: getActivityColor(act.category), flexShrink: 0 }} />
                    {/* Name + distance */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#8B7355', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{act.name}</div>
                      <div style={{ fontSize: '10px', color: '#B8A082', textTransform: 'uppercase', display: 'flex', gap: '6px' }}>
                        <span>{act.category}</span>
                        {distLabel && <span style={{ color: '#c4aa88' }}>· {distLabel}</span>}
                      </div>
                    </div>
                    {isSelected && <span style={{ fontSize: '16px' }}>✓</span>}
                  </div>
                )
              })}
            </div>

            {/* Confirm button */}
            {selectionOrder.length > 0 && selectionOrder.length === total && (
              <button onClick={handleConfirmPlanning}
                style={{
                  width: '100%', marginTop: '14px', padding: '14px', borderRadius: '20px',
                  border: 'none', background: '#C4714A', color: 'white',
                  fontSize: '14px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px'
                }}
              >{t('notes.dayDetail.confirmPlan')}</button>
            )}

            {/* Gastronomy shortcut */}
            <button
              onClick={() => { onClose(); onNavigate('plans') }}
              style={{
                width: '100%', marginTop: '10px', padding: '12px', borderRadius: '20px',
                border: '1.5px solid #e8ddd0', background: 'transparent', color: '#B8A082',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.3px'
              }}
            >
              🍽️ Ver gastronomía local
            </button>
          </>
        )}
      </div>
    )
  }

  // ─── Detail sub-view ─────────────────────────────────────────────────────────

  const renderDetail = () => (
    <div style={{ padding: '0 20px 24px' }}>
      {/* Day navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <button
          onClick={() => currentDay > 1 && setCurrentDay(d => d - 1)}
          style={{
            background: 'none', border: 'none', fontSize: '22px', cursor: currentDay > 1 ? 'pointer' : 'default',
            color: '#8B7355', opacity: currentDay > 1 ? 1 : 0.25, padding: '4px 8px'
          }}
        >←</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#8B7355', letterSpacing: '0.5px' }}>
            {t('mainPage.day')} {currentDay}
          </div>
          <div style={{ fontSize: '11px', color: '#B8A082', marginTop: '2px' }}>{getDateForDay(currentDay)}</div>
        </div>
        <button
          onClick={() => currentDay < tripDuration && setCurrentDay(d => d + 1)}
          style={{
            background: 'none', border: 'none', fontSize: '22px', cursor: currentDay < tripDuration ? 'pointer' : 'default',
            color: '#8B7355', opacity: currentDay < tripDuration ? 1 : 0.25, padding: '4px 8px'
          }}
        >→</button>
      </div>

      {/* Morning block */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
          🌅 {t('notes.dayDetail.morning')}
        </div>
        {morningSlots.map((act, i) => (
          <SlotCard key={act.id || i} activity={act} tripDay={currentDay} userData={userData}
            period="morning"
            onWriteNote={handleWriteNote}
            onReschedule={(a, p) => handleOpenReschedule(a, p)}
            onRemove={(a, p) => handleRemove(a, p)}
          />
        ))}
        {Array.from({ length: 3 - morningSlots.length }).map((_, i) => (
          <EmptySlot key={`em-${i}`} t={t} />
        ))}
      </div>

      {/* Afternoon block */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#B8A082', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
          🌆 {t('notes.dayDetail.afternoon')}
        </div>
        {afternoonSlots.map((act, i) => (
          <SlotCard key={act.id || i} activity={act} tripDay={currentDay} userData={userData}
            period="afternoon"
            onWriteNote={handleWriteNote}
            onReschedule={(a, p) => handleOpenReschedule(a, p)}
            onRemove={(a, p) => handleRemove(a, p)}
          />
        ))}
        {Array.from({ length: 3 - afternoonSlots.length }).map((_, i) => (
          <EmptySlot key={`ea-${i}`} t={t} />
        ))}
      </div>

      {/* Plan this day button */}
      <button
        onClick={() => setSubView('planner')}
        style={{
          width: '100%', padding: '14px', borderRadius: '20px', border: 'none',
          background: '#C4714A', color: 'white', fontSize: '14px',
          fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px',
          boxShadow: '0 4px 12px rgba(212,165,116,0.35)'
        }}
      >{t('notes.dayDetail.planThisDay')} →</button>
    </div>
  )

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
        borderRadius: '24px 24px 0 0',
        width: '100%', maxWidth: '600px',
        maxHeight: '88vh', overflowY: 'auto',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.2)'
      }}>
        {/* Handle bar */}
        <div style={{ padding: '16px 20px 4px', textAlign: 'center' }}>
          <div style={{ width: '40px', height: '4px', background: '#C4A882', borderRadius: '2px', margin: '0 auto 16px' }} />
        </div>

        {subView === 'detail' && renderDetail()}
        {subView === 'planner' && renderPlanner()}
        {subView === 'reschedule' && renderReschedulePicker()}

        {/* Close button */}
        <div style={{ padding: '0 20px 28px' }}>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '13px', background: 'transparent',
              border: '2px solid #C4A882', borderRadius: '20px',
              fontSize: '14px', fontWeight: '600', color: '#8B7355', cursor: 'pointer'
            }}
          >{t('navigation.cancel')}</button>
        </div>
      </div>
    </div>
  )
}
